import React, {createContext, useContext, useEffect, useLayoutEffect, useRef, useState} from "react";

const appContainerId = "app-container";
const fullscreenContainerId = "fullscreen-container";

const handleStyle = isEnteringFullscreen => {
  const appContainer = document.getElementById(appContainerId);
  appContainer.style.visibility = isEnteringFullscreen ? "hidden" : "";
  appContainer.style.overflow = isEnteringFullscreen ? "hidden" : "";

  const fullscreenContainer = document.getElementById(fullscreenContainerId);
  fullscreenContainer.style.display = isEnteringFullscreen ? "block" : "none";

  const dialogs = document.getElementsByClassName("MuiDialog-root");
  for (let i = 0; i < dialogs.length; i++) {
    dialogs[i].style.display = isEnteringFullscreen ? "none" : "block";
  }
  const drawers = document.getElementsByClassName("MuiDrawer-root");
  for (let i = 0; i < drawers.length; i++) {
    drawers[i].style.display = isEnteringFullscreen ? "none" : "block";
  }
};

// Context for fullscreen state and toggle handler, with default values
const FullscreenContext = createContext({
  isFullscreen: false,
  handleFullscreen: () => {}
});

// Hook to access fullscreen context values and actions
export const useFullscreen = () => useContext(FullscreenContext);

// Provider component that manages fullscreen state and styling for its children
const FullscreenProvider = ({children, onFullscreenToggle}) => {
  const containerRef = useRef(null);
  const elementRef = useRef(null);

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [target, setTarget] = useState(null);

  // On unmount, ensure fullscreen mode is exited and styles are reset
  useEffect(() => {
    return () => {
      setIsFullscreen(false);
      setTarget(null);

      handleStyle(false);

      if (onFullscreenToggle) onFullscreenToggle(false);
    };
  }, [onFullscreenToggle]);

  useLayoutEffect(() => {
    const node = elementRef.current;

    if (!node || !target) return;

    if (node.parentElement !== target) {
      target.appendChild(node);
    }

    return () => {
      // Ensure the parent still exists and still contains this node
      // (prevents errors if the DOM changed) before removing the node.
      if (node.parentElement && node.parentElement.contains(node)) {
        node.parentElement.removeChild(node);
      }
    };
  }, [target]);

  // Toggle fullscreen mode and update styles/state
  const handleFullscreen = () => {
    const isEnteringFullscreen = !isFullscreen;

    setIsFullscreen(isEnteringFullscreen);
    setTarget(isEnteringFullscreen ? document.getElementById(fullscreenContainerId) : containerRef.current);

    handleStyle(isEnteringFullscreen);

    if (onFullscreenToggle) onFullscreenToggle(isEnteringFullscreen);
  };

  return (
    <FullscreenContext.Provider value={{isFullscreen, handleFullscreen}}>
      <div ref={containerRef} style={{width: "100%", height: "100%"}}>
        <div ref={elementRef} style={{width: "100%", height: "100%"}}>
          {children}
        </div>
      </div>
    </FullscreenContext.Provider>
  );
};

export default FullscreenProvider;
